import { Controller } from "@hotwired/stimulus";
import {
  AXIS_TITLE,
  DEFAULT_COLOR,
  FORMATTERS,
  LINE_BASE_COLOR,
  SERIES_PALETTE,
  Y1_AXIS_TITLE,
  Y_AXIS_TITLE,
  applyOpacity,
  compactDateLabel,
  labelsSpanMultipleYears,
  truncateLabel,
  LABEL_ROOM_PX,
  targetLinesPlugin,
  valueLabelsPlugin,
  xTickDensity,
} from "../charts/shared_config.mjs";

// Re-exported for the existing unit tests, which import it from this module.
export { compactDateLabel };


// Chart.js cannot scroll a tooltip, so a wide breakout is capped to the largest series at that x.
const TOOLTIP_MAX_ITEMS = 12;

// chart.js is ~0.5MB. Load it lazily so it leaves the eagerly-loaded controllers bundle and is only
// fetched on pages that actually render a chart, rather than on every admin page load. Memoized so
// the import + one-time plugin registration happen at most once per page.
let chartPromise;
const loadChart = () => {
  chartPromise ||= import("chart.js/auto").then(({ default: Chart }) => {
    Chart.register(targetLinesPlugin);
    Chart.register(valueLabelsPlugin);
    return Chart;
  });
  return chartPromise;
};

export default class extends Controller {
  static targets = ["canvas", "empty", "labels", "legend"];
  static values = {
    type: { type: String, default: "bar" },
    panels: Object,
    selected: String,
    formatter: { type: String, default: "integer" },
    xAxisLabel: String,
    yAxisLabel: String,
    yTickStep: Number,
    color: String,
    opacity: { type: Number, default: 0.7 },
    stacked: Boolean,
    colors: Object,
    seriesLabels: Object,
    seriesOrder: Array,
    seriesTypes: Object,
    seriesAxes: Object,
    seriesFormats: Object,
    rightAxisLabel: String,
    targetLines: Array,
    // Print each mark's value above it, as Metabase's graph.show_values does.
    valueLabels: Boolean,
    points: { type: Boolean, default: true },
    // Hover-only precision override. Unset everywhere but the realtime board, where axes stay
    // whole-dollar and hovers carry cents. Falls between the per-series format and the axis format.
    tooltipFormatter: String,
    // Suffix for a row's optional `count`, appended to its hover ("$70.31 · 3 items"). Opt-in: with
    // no counts on the rows, nothing changes.
    countLabel: String,
    // Drop series with nothing in them from the hover. Opt-in, because a genuine zero is meaningful on
    // some charts (a line dipping to 0) -- it is only noise where series are ZERO-FILLED to hold their
    // place in a stack, which is why the realtime board fills them in the first place.
    hideZeroInTooltip: Boolean,
    // Keep a stacked chart's series in their declared order instead of re-sorting by total. On a board
    // that updates in place, total-ordering makes segments swap position as money lands -- motion that
    // reads as a glitch rather than as information.
    stableStackOrder: Boolean,
  };

  async connect() {
    // Per-connect token, not a shared flag: Stimulus reuses the instance across reconnects, so the
    // lazy import() must only build the chart for the latest connect() (and skip after teardown).
    const token = {};
    this.connectToken = token;
    const Chart = await loadChart();
    if (this.connectToken !== token) return;

    const format = FORMATTERS[this.formatterValue] || FORMATTERS.integer;
    const controller = this;
    const seriesAt = (index) => this.renderedSeries?.[index];
    // chartData() populates renderedSeries, so build it before options read multiSeries.
    const data = this.chartData();
    this.chart = new Chart(this.canvasTarget, {
      type: this.typeValue,
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        // Value labels print just outside the extreme marks, which sit flush with the plot edge.
        // Padding the layout gives them room without widening the axis range into a new tick.
        layout: { padding: this.valueLabelsValue ? { top: LABEL_ROOM_PX, bottom: LABEL_ROOM_PX } : 0 },
        // Index-mode hover whenever there's more than one series (comparing them at an x is the
        // whole point), or when markers are hidden and the per-point hit target is tiny.
        interaction:
          this.pointsValue && !this.multiSeries
            ? {}
            : { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          targetLines: { lines: this.targetLinesValue },
          valueLabels: {
            enabled: this.valueLabelsValue,
            stacked: this.stackedValue,
            // The same formatter the axis uses, so a label never disagrees with its tick.
            format: (value, datasetIndex) => this.formatterFor(seriesAt(datasetIndex))(value),
          },
          tooltip: {
            usePointStyle: true,
            // Largest first, so a capped tooltip shows the series that matter at this x.
            itemSort: (a, b) => (b.parsed.y ?? 0) - (a.parsed.y ?? 0),
            filter: (item, _index, items) => {
              const shown = this.hideZeroInTooltipValue ? items.filter((i) => this.tooltipEligible(i)) : items;
              if (!shown.includes(item)) return false;

              this.tooltipSeriesCount = shown.length;
              if (shown.length <= TOOLTIP_MAX_ITEMS) return true;

              // Rank within the item's own unit group: a right-axis rate at 0.5 is not "smaller" than a
              // count of 90, and comparing their raw magnitudes would drop it from every tooltip.
              const group = (i) => this.tooltipUnitGroup(seriesAt(i.datasetIndex));
              const mine = group(item);
              const peers = shown.filter((other) => group(other) === mine);
              // Filter runs before itemSort, so rank here rather than relying on item order.
              // datasetIndex breaks ties so equal values still cut to exactly the cap.
              const rank = peers.filter((other) => {
                const dy = (other.parsed.y ?? 0) - (item.parsed.y ?? 0);
                return dy > 0 || (dy === 0 && other.datasetIndex < item.datasetIndex);
              }).length;
              return rank < this.tooltipGroupBudget(shown, group)[mine];
            },
            displayColors: (ctx) => ctx.chart.data.datasets.length > 1,
            callbacks: {
              // All items at a hovered index share one x label, so show it once.
              title: (items) => items[0]?.label,
              // beforeBody sees the already-filtered list, so filter records the eligible total.
              beforeBody: () =>
                (this.tooltipSeriesCount > TOOLTIP_MAX_ITEMS
                  ? `Top ${TOOLTIP_MAX_ITEMS} of ${this.tooltipSeriesCount} series`
                  : []),
              label: (ctx) => {
                const value = this.tooltipFormatterFor(
                  seriesAt(ctx.datasetIndex),
                )(ctx.parsed.y);
                const named =
                  ctx.chart.data.datasets.length > 1
                    ? `${ctx.dataset.label}: ${value}`
                    : value;
                // Counts ride on the dataset, so they survive chart.update() and panel switches.
                const count = ctx.dataset.counts?.[ctx.dataIndex];
                if (count == null || !this.hasCountLabelValue) return named;

                const unit =
                  count === 1
                    ? this.countLabelValue.replace(/s$/, "")
                    : this.countLabelValue;
                return `${named} · ${new Intl.NumberFormat().format(count)} ${unit}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            stacked: this.stackedValue,
            title: this.hasXAxisLabelValue
              ? { display: true, text: this.xAxisLabelValue, ...AXIS_TITLE }
              : { display: false },
            ticks: {
              // A rotated tick costs sin(angle) x its width in height, so only rotate when needed.
              minRotation: 0,
              maxRotation: 45,
              ...this.xTickDensity(),
              padding: 2,
              font: { size: 10 },
              // Read per call, not closed over: panel switches swap rows without rebuilding.
              callback(value) {
                const raw = this.getLabelForValue(value);
                const withYear = controller.xLabelsSpanMultipleYears();
                return truncateLabel(
                  compactDateLabel(raw, { withYear }) ?? raw,
                );
              },
            },
          },
          y: {
            beginAtZero: true,
            stacked: this.stackedValue,
            suggestedMax: this.targetLineMax(),
            suggestedMin: this.targetLineMin(),
            title: this.hasYAxisLabelValue
              ? { display: true, text: this.yAxisLabelValue, ...Y_AXIS_TITLE }
              : { display: false },
            ticks: {
              stepSize: this.hasYTickStepValue
                ? this.yTickStepValue
                : undefined,
              padding: 1,
              callback: (v) => format(v),
            },
          },
          ...(this.hasRightAxis() ? { y1: this.rightScale() } : {}),
        },
      },
    });
    this.syncEmptyAndLabels();
    this.renderLegend();
    this.observeResize();
  }

  disconnect() {
    this.connectToken = null;
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.resizeFrame);
    this.chart?.destroy();
    this.chart = null;
  }

  // Chart.js' own responsive observer drops the resize when the container *grows* mid-animation, so the
  // canvas stays narrow after the Mo chat panel closes (or is dragged wider) until the next full render.
  // A plain resize() on every settled frame covers that case.
  observeResize() {
    if (typeof ResizeObserver === "undefined") return;
    this.resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.chart?.resize());
    });
    this.resizeObserver.observe(this.canvasTarget.parentElement);
  }

  switch(event) {
    this.selectedValue = event.target.value;
  }

  // Fires when the panel data attribute is rewritten in place (the realtime board's poller does
  // this), and never on pages that render once -- Stimulus only calls it on actual mutation, and the
  // !chart guard inside skips the initialization call. Delegating to selectedValueChanged means
  // in-place data updates tween through chart.update() exactly like the panel switcher always has.
  panelsValueChanged() {
    this.selectedValueChanged();
  }

  selectedValueChanged() {
    if (!this.chart) return;
    this.applyData(this.chartData());
    // chartData() refreshed renderedSeries, so the new panel may gain or lose its right-axis series.
    if (this.hasRightAxis()) this.chart.options.scales.y1 = this.rightScale();
    else delete this.chart.options.scales.y1;
    Object.assign(this.chart.options.scales.x.ticks, this.xTickDensity());
    this.chart.update();
    this.syncEmptyAndLabels();
    this.renderLegend();
  }

  // KEEP THE DATASET OBJECTS, replace what is inside them. Assigning `chart.data` wholesale hands
  // Chart.js brand-new dataset objects, so it rebuilds each controller and loses the element positions
  // it animates FROM -- every bar then grows up from zero on a routine update. On the realtime board
  // that fires whenever a sale lands, which is the one thing that page must never do. Mutating the
  // existing datasets in place keeps their identity, so update() tweens from the values on screen.
  //
  // Only when the shape matches: a different series count or a renamed series really is a different
  // chart, and there is nothing meaningful to tween from.
  applyData(next) {
    const current = this.chart.data;
    // Both sides have to BE a dataset list before their shapes can be compared -- a chart mid-init has
    // no data yet. Anything uncomparable falls through to the wholesale assignment, which is what this
    // always did.
    const currentSets = current?.datasets;
    const nextSets = next?.datasets;
    const sameShape =
      Array.isArray(currentSets) &&
      Array.isArray(nextSets) &&
      currentSets.length === nextSets.length &&
      currentSets.every((ds, i) => ds.label === nextSets[i].label);

    if (!sameShape) {
      this.chart.data = next;
      return;
    }

    current.labels = next.labels;
    nextSets.forEach((ds, i) => {
      const target = currentSets[i];
      // Drop keys the new dataset does not carry, so a series that loses (say) its per-point counts
      // does not keep the old ones -- Object.assign alone would merge them forward.
      for (const key of Object.keys(target)) {
        if (!(key in ds)) delete target[key];
      }
      Object.assign(target, ds);
    });
  }

  renderLegend() {
    if (!this.hasLegendTarget) return;
    const datasets = this.chart.data.datasets;
    if (datasets.length <= 1) {
      this.legendTarget.classList.add("hidden");
      this.legendTarget.replaceChildren();
      return;
    }
    this.legendTarget.classList.remove("hidden");
    this.legendTarget.replaceChildren(
      ...datasets.map((ds) => {
        const item = document.createElement("div");
        item.className = "flex items-center gap-2 whitespace-nowrap";
        const swatch = document.createElement("span");
        swatch.className = "h-2 w-2 shrink-0 rounded-full";
        swatch.style.backgroundColor = ds.borderColor || ds.backgroundColor;
        const label = document.createElement("span");
        label.className = "truncate";
        label.textContent = ds.label;
        item.append(swatch, label);
        return item;
      }),
    );
  }

  chartData() {
    const rows = this.currentRows();
    if (rows.some((r) => r.series != null)) return this.multiSeriesData(rows);
    return this.singleSeriesData(rows);
  }

  singleSeriesData(rows) {
    const labels = rows.map((r) => r.label);
    const data = rows.map((r) => r.value);
    const baseColor = this.hasColorValue ? this.colorValue : DEFAULT_COLOR;
    const colors = rows.map((r) =>
      applyOpacity(r.color || baseColor, this.opacityValue),
    );
    const lineColor = this.hasColorValue
      ? applyOpacity(this.colorValue, this.opacityValue)
      : LINE_BASE_COLOR;

    const counts = rows.some((r) => r.count != null)
      ? rows.map((r) => r.count)
      : null;
    const dataset =
      this.typeValue === "line"
        ? {
            data,
            borderColor: lineColor,
            backgroundColor: lineColor,
            pointBackgroundColor: colors,
            pointBorderColor: colors,
            pointRadius: this.pointsValue ? 4 : 0,
            pointHoverRadius: 6,
            tension: 0.3,
            fill: false,
            segment: {
              borderColor: (ctx) => colors[ctx.p1DataIndex] ?? lineColor,
            },
          }
        : {
            data,
            backgroundColor: colors,
            borderRadius: 4,
            categoryPercentage: 1.0,
            barPercentage: 0.8,
          };

    if (counts) dataset.counts = counts;
    this.renderedSeries = [];
    return { labels, datasets: [dataset] };
  }

  get multiSeries() {
    return (this.renderedSeries?.length ?? 0) > 1;
  }

  // Overrides outlive the series they belong to: a long-format series can be rolled into "Other" or
  // filtered out while its saved axis remains, which would otherwise draw an empty secondary axis.
  rightAxisSeries() {
    const axes = this.seriesAxesValue || {};
    return (this.renderedSeries || []).filter((s) => axes[s] === "right");
  }

  hasRightAxis() {
    return this.rightAxisSeries().length > 0;
  }

  // How many tooltip rows each unit group gets: one guaranteed, then the remaining slots handed out
  // largest-group-first so a wide breakout keeps most of the cap and a lone rate line still shows.
  tooltipGroupBudget(items, group) {
    const sizes = new Map();
    items.forEach((i) => sizes.set(group(i), (sizes.get(group(i)) || 0) + 1));

    const budget = {};
    sizes.forEach((_size, key) => (budget[key] = 1));
    let left = TOOLTIP_MAX_ITEMS - sizes.size;
    // Biggest groups first, and only up to what each can actually use.
    const bySize = [...sizes.entries()].sort((a, b) => b[1] - a[1]);
    while (left > 0 && bySize.some(([key, size]) => budget[key] < size)) {
      for (const [key, size] of bySize) {
        if (left <= 0) break;
        if (budget[key] < size) {
          budget[key] += 1;
          left -= 1;
        }
      }
    }
    return budget;
  }

  // Series sharing an axis and a number format are comparable; series that don't are ranked separately
  // so a small-magnitude unit (a 0..1 rate beside raw counts) still reaches the tooltip.
  tooltipUnitGroup(series) {
    const axis = (series && this.seriesSetting("axis", series)) || "left";
    const format = (series && this.seriesSetting("format", series)) || this.formatterValue;
    return `${axis}:${format}`;
  }

  // A zero-filled series draws nothing, so listing it in the hover is noise. A zero VALUE with a
  // non-zero count is kept: a fully-discounted wash really did sell for $0.00.
  tooltipEligible(item) {
    return Boolean(item.parsed.y) || Boolean(item.dataset.counts?.[item.dataIndex]);
  }

  // The hover chain: per-series format, then the chart's tooltip override, then the axis format.
  // With tooltipFormatter unset this is exactly formatterFor, which is what every existing chart has.
  tooltipFormatterFor(series) {
    const name = series && this.seriesSetting("format", series);
    return (
      FORMATTERS[name] ||
      FORMATTERS[this.tooltipFormatterValue] ||
      FORMATTERS[this.formatterValue] ||
      FORMATTERS.integer
    );
  }

  formatterFor(series) {
    const name = series && this.seriesSetting("format", series);
    return (
      FORMATTERS[name] || FORMATTERS[this.formatterValue] || FORMATTERS.integer
    );
  }

  // Right-axis ticks follow the first series assigned to it; mixed formats on one axis are the
  // author's problem, not something to guess at.
  // Built on demand rather than inlined at construction: a panel switch can change whether any
  // rendered series uses the right axis, so the scale has to be added or dropped to match.
  rightScale() {
    return {
      position: "right",
      beginAtZero: true,
      // Stacking is a chart-level toggle, so the secondary axis has to honour it too --
      // otherwise right-axis bars overlap while left-axis ones stack.
      stacked: this.stackedValue,
      grid: { drawOnChartArea: false },
      title: this.hasRightAxisLabelValue
        ? { display: true, text: this.rightAxisLabelValue, ...Y1_AXIS_TITLE }
        : { display: false },
      ticks: { callback: (v) => this.rightFormatter()(v) },
    };
  }

  rightFormatter() {
    return this.formatterFor(this.rightAxisSeries()[0]);
  }

  seriesSetting(name, series) {
    const maps = {
      color: this.colorsValue,
      label: this.seriesLabelsValue,
      type: this.seriesTypesValue,
      axis: this.seriesAxesValue,
      format: this.seriesFormatsValue,
    };
    return maps[name]?.[series];
  }

  multiSeriesData(rows) {
    const labels = [...new Set(rows.map((r) => r.label))];
    const present = new Set(rows.map((r) => r.series));
    // Ordering drives palette assignment too, so colors track the declared order.
    const declared = (this.seriesOrderValue || []).filter((s) =>
      present.has(s),
    );
    // Undeclared series keep row order: the SQL's ORDER BY is the ranking, and re-sorting by name
    // presented it as arbitrary.
    const seriesInOrder = [...declared, ...[...present].filter((s) => !declared.includes(s))];
    const colorBySeries = new Map(
      seriesInOrder.map((s, i) => [
        s,
        this.seriesSetting("color", s) ||
          SERIES_PALETTE[i % SERIES_PALETTE.length],
      ]),
    );

    // Stacked bars draw bottom-up, so sort puts the largest on top. Colors are already bound above.
    let seriesNames = seriesInOrder;
    if (this.stackedValue && !this.stableStackOrderValue) {
      const totals = new Map();
      rows.forEach((r) =>
        totals.set(r.series, (totals.get(r.series) || 0) + r.value),
      );
      seriesNames = [...seriesInOrder].sort(
        (a, b) => (totals.get(b) || 0) - (totals.get(a) || 0),
      );
    }

    const valueByLabelBySeries = new Map(labels.map((l) => [l, new Map()]));
    rows.forEach((r) =>
      valueByLabelBySeries.get(r.label).set(r.series, r.value),
    );
    const anyCounts = rows.some((r) => r.count != null);
    const countByLabelBySeries = new Map(labels.map((l) => [l, new Map()]));
    rows.forEach((r) =>
      countByLabelBySeries.get(r.label).set(r.series, r.count),
    );
    // Legend/tooltip show the display label when one is set for this series value; else the raw value.
    // An explicitly blank label is a label — Metabase uses it to suppress the legend entry — so only a
    // missing key falls through to the raw value.
    const labelFor = (series) => this.seriesLabelsValue?.[series] ?? series;

    this.renderedSeries = seriesNames;
    const datasets = seriesNames.map((series) => {
      const color = applyOpacity(colorBySeries.get(series), this.opacityValue);
      const data = labels.map(
        (label) => valueByLabelBySeries.get(label).get(series) ?? null,
      );
      const counts = anyCounts
        ? labels.map(
            (label) => countByLabelBySeries.get(label).get(series) ?? null,
          )
        : null;
      const isLine =
        (this.seriesSetting("type", series) || this.typeValue) === "line";
      const yAxisID =
        this.seriesSetting("axis", series) === "right" ? "y1" : "y";
      // Chart.js draws lower `order` first, i.e. furthest back: a line behind a taller bar disappears.
      const drawOrder = isLine ? 0 : 1;
      // counts is attached after the branch so both mark types carry it without duplicating the key.
      const dataset = isLine
        ? {
            type: "line",
            yAxisID,
            order: drawOrder,
            label: labelFor(series),
            data,
            borderColor: color,
            borderWidth: 2.5,
            backgroundColor: color,
            pointBackgroundColor: color,
            pointBorderColor: color,
            pointRadius: this.pointsValue ? 3 : 0,
            pointHoverRadius: 5,
            tension: 0.3,
            // Stacked areas are a line-chart idea. A line override on a stacked BAR chart is an overlaid
            // rate, so filling it would hide the bars underneath.
            fill: this.typeValue === "line" && this.stackedValue,
          }
        : {
            type: "bar",
            yAxisID,
            order: drawOrder,
            label: labelFor(series),
            data,
            backgroundColor: color,
            borderRadius: 4,
            categoryPercentage: 0.9,
            // Bars in a group sit nearly flush so the group reads as one unit, while the slack
            // categoryPercentage leaves keeps the groups themselves clearly apart.
            barPercentage: 0.95,
            // Grouped (non-stacked) only: collapse missing series slots so present bars center on
            // their x tick. Stacked bars share one column per tick, so the default behavior is fine there.
            skipNull: !this.stackedValue,
          };
      if (counts) dataset.counts = counts;
      return dataset;
    });

    return { labels, datasets };
  }

  currentRows() {
    const key = this.selectedValue || Object.keys(this.panelsValue)[0];
    return this.panelsValue[key] || [];
  }

  // Distinct labels, because multi-series data repeats each label once per series: a row count would
  // call a 10-date two-series chart dense when the axis only has 10 ticks.
  xLabels() {
    return [
      ...new Set(this.currentRows().map((row) => String(row.label ?? ""))),
    ];
  }

  xTickDensity() {
    return xTickDensity(this.xLabels());
  }

  // "Jul 13" is ambiguous once the range straddles a year boundary.
  xLabelsSpanMultipleYears() {
    return labelsSpanMultipleYears(this.xLabels());
  }

  targetLineMax() {
    if (!this.targetLinesValue?.length) return undefined;
    return Math.max(...this.targetLinesValue.map((l) => l.value));
  }

  targetLineMin() {
    if (!this.targetLinesValue?.length) return undefined;
    return Math.min(...this.targetLinesValue.map((l) => l.value));
  }

  syncEmptyAndLabels() {
    const rows = this.currentRows();
    if (this.hasEmptyTarget) this.emptyTarget.hidden = rows.length > 0;
    this.canvasTarget.hidden = rows.length === 0;
    const key = this.selectedValue || Object.keys(this.panelsValue)[0];
    this.labelsTargets.forEach((ul) => {
      ul.hidden = ul.dataset.panelKey !== key;
    });
  }
}
